<template>
  <v-container fill-height>
    <div v-if="!attendance" class="d-flex align-center justify-center my-10">
      <v-progress-circular color="primary" indeterminate />
    </div>

    <v-row v-else class="fill-height" justify="center" no-gutters>
      <v-col cols="12" sm="8" md="5" lg="4" xl="3">
        <CustomChat
          :chat="_chat"
          :attendant="{
            name: attendance.attendant.name,
            icon: 'mdi-account',
          }"
          :height="'80vh'"
          :finalizeLabel="'Finalizar Atendimento'"
          :readMode="readMode"
          :disabled="_requesterChatDisabled"
          :placeholder="''"
          @send-message="handleSendMessage($event)"
          @finalize="finalizeAttendance()"
          @attach-file="uploadFile($event)"
        />

        <div v-if="readMode" class="d-flex align-center justify-center mt-4">
          <v-btn @click="finalizeAttendance()" class="text-none" text>
            Finalizar
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { getAttendance } from "@/services/attendance";
import { uploadFile } from "@/services/files";
import { handleAlert, showError, handleFinishAttendance } from "@/utils";
import { eventBus } from "@/eventBus";
import CustomChat from "@/components/customChat/CustomChat";

export default {
  name: "Attendance",

  components: {
    CustomChat,
  },

  data() {
    return {
      interval: null,
      readMode: false,
      currentStatus: "",
      requesterChatEnabled: null,
      chat: {
        messages: [],
      },
      transferringAppointment: false,
    };
  },

  mounted() {
    document.addEventListener("touchstart", this.handleTouchStart);
    document.addEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    });

    this.handleAttendance();

    //eventBus.$on("appointments:transferred", this.onAppointmentTransferred);
    eventBus.$on("attendance:transferred", this.onTransferredToQueue);
    eventBus.$on("attendance:finished", this.onAttendanceFinished);
  },

  beforeDestroy() {
    document.removeEventListener("touchstart", this.handleTouchStart);
    document.removeEventListener("touchmove", this.handleTouchMove);

    clearInterval(this.interval);

    //eventBus.$off("appointments:transferred", this.onAppointmentTransferred);
    eventBus.$off("attendance:transferred", this.onTransferredToQueue);
    eventBus.$off("attendance:finished", this.onAttendanceFinished);
  },

  computed: {
    ...mapState("socket", [
      "attendance",
      "messages",
      "inAttendance",
      "inQueue",
      "code",
    ]),

    _code() {
      return this.$route.query.code || this.attendance?.code || this.code;
    },

    _chat() {
      return {
        ...this.chat,
        messages: this.messages,
      };
    },

    /**
     * Resolvido pela API na hierarquia balcão > vara > comarca.
     *
     * Quando nenhum nível configurou, a API devolve `undefined` e cada fork
     * aplica o próprio default. Aqui o default é **bloquear**, que é como o
     * TJ-PB sempre operou — antes disso era um `:disabled="true"` fixo. Assim o
     * deploy não muda o comportamento de ninguém: a digitação só abre onde o
     * tribunal ligar a flag. Ver ADR-008.
     */
    _requesterChatDisabled() {
      return this.requesterChatEnabled !== true;
    },
  },

  watch: {
    attendance(val, oldVal) {
      if (oldVal && val && oldVal.desk?._id !== val.desk?._id) {
        this.handleDeskTransfer(val);
      }
    },
  },

  methods: {
    ...mapActions("socket", [
      "setAttendance",
      "joinAttendance",
      "finishAttendance",
      "sendMessage",
      "setMessages",
      "addMessage",
      "syncMessages",
      "resetAttendance",
      "setInQueue",
      "setAppointment",
    ]),

    // Rede de segurança: a API também devolve o histórico completo. Se o
    // socket ficou fora e perdeu mensagens, o poll de 60s recupera.
    resyncMessagesFromApi(messages) {
      if (!messages?.length) return;
      if (messages.length === this.messages.length) return;

      this.syncMessages(messages);
    },

    handleSendMessage(data) {
      const payload = {
        attendanceID: this.attendance._id,
        text: data.message,
      };

      this.sendMessage({
        data: payload,
        callback: (error) => {
          if (error) {
            console.log("sendMessage:error", error);
            throw new Error(error);
          }
        },
      });
    },

    async uploadFile(event) {
      try {
        let formData = new FormData();
        formData.append("file", event.file);

        const { data } = await uploadFile(formData);
        this.handleSendMessage({ message: data.publicUrl });
      } catch (error) {
        this.handleAlert(
          error.response?.data?.message || error.data.message,
          "error"
        );
      }
    },

    handleAttendance() {
      this.getAttendance();

      this.interval = setInterval(() => {
        this.getAttendance();
      }, 60000);
    },

    async getAttendance() {
      try {
        const { data } = await getAttendance(this.attendance._id);

        this.requesterChatEnabled = data.requesterChatEnabled ?? null;
        this.resyncMessagesFromApi(data.messages);

        if (!this.currentStatus) {
          this.currentStatus = data.status;
          return;
        }

        if (
          this.currentStatus === "in-progress" &&
          data.status === "finished"
        ) {
          this.handleAlert("Atendimento finalizado por inatividade", "info");

          const params = {
            attendanceID: data._id,
            deskID: data.desk._id,
          };
          const query = {
            code: data.code || this._code,
          };

          this.handleRating(params, query);
          return;
        }
      } catch (error) {
        this.handleAlert(
          error.response?.data?.message || error.data.message,
          "error"
        );

        this.resetAttendance();
        this.$router.push({ name: "Home" });
      }
    },

    handleJoin() {
      try {
        const payload = {
          protocol: this.attendance.protocol,
        };

        this.joinAttendance({
          data: payload,
          callback: (error, data) => {
            if (error) {
              console.log("joinAttendance:error", error);
              throw new Error(error);
            }

            if (data.messages) {
              this.setMessages([]);

              data.messages.forEach((el) => {
                this.addMessage(el);
              });
            }
          },
        });
      } catch (error) {
        this.showError(error);
      }
    },

    finalizeAttendance() {
      const msg = {
        message: "Atendimento finalizado pelo solicitante",
      };

      const payload = {
        attendanceID: this.attendance._id,
        deskID: this.attendance.desk._id,
      };

      this.handleSendMessage(msg);
      this.handleFinishAttendance();

      this.handleRating(payload);
    },

    handleRating(params, query) {
      if (this.transferringAppointment) return;

      this.$router.push(
        {
          name: "Rating",
          params,
          query,
        },
        () => {},
        () => {}
      );
    },

    handleTouchStart(event) {
      this.startY = event.touches[0].clientY;
    },

    handleTouchMove(event) {
      const currentY = event.touches[0].clientY;

      if (currentY > this.startY && window.scrollY === 0) {
        event.preventDefault();
      }
    },

    onAppointmentCreated(data) {
      const appointment = data.appointment || data;

      if (appointment?.autoScheduled === true) {
        this.setAppointment(appointment);
        this.setInQueue(true);

        this.$router
          .push({ path: `/queue-appointments/${appointment._id}` })
          .catch(() => {});
      }
    },

    onTransferredToQueue(data) {
      const desk = data?.desk;
      const operationMode = desk?.operationMode;

      if (!operationMode || operationMode === "queue") {
        this.setInQueue(true);
        this.$router.push({ path: "/queue" }).catch(() => {});
      }
    },

    onAttendanceFinished(data) {
      if (data.skipRating) return;

      const params = {
        attendanceID: data._id,
        deskID: data.desk._id,
      };
      const query = {
        code: data.code || this._code,
      };

      this.handleAlert("Atendimento finalizado pelo atendente", "info");

      this.handleRating(params, query);
    },

    showError,
    handleAlert,
    handleFinishAttendance,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
